// utils.js

import React, { useState, useEffect,  useRef, useCallback } from 'react';

import axios from 'axios';
import API_BASE_URL from '../config';
import jsPDF from 'jspdf';
import _ from 'lodash';
import 'jspdf-autotable';
import { formatDistanceToNow } from 'date-fns';


export const formatShortDateTime = (date) => { // format dte to dd-mm-yyyy:hh-mm
  const now = new Date(date); // Ensure the date is a Date object
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = now.getFullYear();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `${day}-${month}-${year}:${hours}:${minutes}`;
};

export const formatMediumDate = (isoDateString) => {
  // Check if the input is a valid string and not empty
  if (typeof isoDateString !== 'string' || isoDateString.trim() === '') {
    console.warn('Invalid date string: must be a non-empty string');
    return ''; // or handle it however you prefer
  }

  const date = new Date(isoDateString);

  // Validate the Date object
  if (isNaN(date.getTime())) {
    console.warn('Invalid Date object for input:', isoDateString);
    return ''; // or handle it however you prefer
  }

  // Define the date format options
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short', // Sep
    day: 'numeric', // 12
    year: 'numeric', // 2024
  });

  return dateFormatter.format(date);
};



export const formatLongDateTime = (isoDateString) => {// Convert ISO date string like  Sep 8, 10:44 PM
  const date = new Date(isoDateString);

  // Validate the Date object
  if (!(date instanceof Date) || isNaN(date.getTime())) {throw new Error('Invalid Date object');}

  // Define the date and time format options
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return dateFormatter.format(date);
};
export const inputDate = (timestamp) => { // format date for input
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based in JavaScript
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export const formatFriendlyDate = dateStr => {
  const formattedDate = formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  return formattedDate.replace(/^about\s/, ''); // Remove "about" if present
};

export const formatCurrency=(amount, currencyCode = 'MKW') =>{ //Formats a number as a currency string.
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(amount);
}




export const formatTwoDecimal = (amount, locale = 'en-US') => {
  // Ensure the input is treated as a number
  const numberValue = parseFloat(amount);
  
  // Check for invalid inputs and return a fallback string
  if (isNaN(numberValue)) {return '0.00';}

  try {
      return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          // You can add style: 'currency' and currency: 'USD' if needed, 
          // but for simple grouping like '260,000.00', these options are enough.
      }).format(numberValue);
  } catch (error) {
      console.error("Formatting error:", error);
      return numberValue.toFixed(2); // Fallback to simple fixed decimals
  }
};




export const formatNumber = (value) => { // convert to number
  const parsed = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
  return isNaN(parsed) ? null : parsed;
};


export  const  formatToTwoDecimals = (value) => {  // Adjust number of decimal places
  return new Intl.NumberFormat('en-US', {

    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};



export const getFirstAndLastDateOfMonth = () => { // first and last date of the current month
  const now = new Date(); // Get the current date
  const firstDate = new Date(now.getFullYear(), now.getMonth(), 1); // First date of the current month
  const lastDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last date of the current month
  // Format the dates as 'YYYY-MM-DD'
  const startDate = firstDate.toISOString().split('T')[0];
  const endDate = lastDate.toISOString().split('T')[0];
  return { startDate, endDate };
};

export const normalizeDate = (date) => { // takes care of server / client time zone differences
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0); // Set time to midnight to ignore time during comparison
  return normalized;
};

 export const  getFirstAndLastDayOfWeek=(weekStart = 'sunday')=> { // first and last date of current week
  const today = new Date();
  const dayOfWeek = today.getDay();

  // Adjust for week start on Sunday (index 0)
  const offset = (dayOfWeek + 7 - (weekStart === 'sunday' ? 0 : 1)) % 7;

  const firstDayOfWeek = new Date(today);
  firstDayOfWeek.setDate(today.getDate() - offset);

  const lastDayOfWeek = new Date(firstDayOfWeek);
  lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
 


  return { 
    firstDayOfWeek: normalizeDate(firstDayOfWeek), 
    lastDayOfWeek: normalizeDate(lastDayOfWeek) 
  };
 
}

export const  formatDateToMonth = (dateString) => { // return array of short months
  const date = new Date(dateString);
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return monthNames[date.getMonth()];
}

export const capitalize = (name) => {
  if (typeof name !== 'string' || name === null) {
    return ''; // Return an empty string immediately to prevent the error.
  }

  // Convert the string to lowercase, split it by spaces, and filter out any empty strings
  // that might result from multiple spaces (e.g., "hello  world").
  return name
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    // For each word, capitalize the first letter and then add the rest of the word.
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    // Join the words back together with a space.
    .join(' ');
};

export const useToast = () => {
  const [toastMessage, setToastMessage] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    // If the toast is shown, set a timer to hide it after 3 seconds.
    if (toastMessage.show) {
      const timer = setTimeout(() => {
        setToastMessage({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer); // Clear the timer if the component unmounts or toast state changes
    }
  }, [toastMessage]);

  /**
   * Shows a toast notification with the given message and type.
   * @param {string} message The message to display in the toast.
   * @param {string} type The type of toast (e.g., 'info', 'success', 'error'). Defaults to 'info'.
   */
  const showToast = (message, type = 'info') => {
    setToastMessage({ show: true, message, type });
  };

  return { showToast, toastMessage };
};


export const AudioSpinner = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 100"
    preserveAspectRatio="xMidYMid"
    width="80"
    height="80"
    className="mx-auto"
  >
    <circle
      cx="50"
      cy="50"
      fill="none"
      stroke="#3b82f6"
      strokeWidth="10"
      r="35"
      strokeDasharray="164.93361431346415 56.97787143782138"
      transform="rotate(270 50 50)"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        repeatCount="indefinite"
        dur="1s"
        values="0 50 50;360 50 50"
        keyTimes="0;1"
      ></animateTransform>
    </circle>
  </svg>
);

// The fixed SearchableDropdown component
export const SearchableDropdown = ({ items, onSelect, placeholder, selectedItem }) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  // FIX: This new useEffect ensures that `filteredItems` is always in sync with the `items` prop
  // whenever the `items` prop changes (e.g., when the parent component fetches data).
  useEffect(() => {
    setFilteredItems(items);
  }, [items]);

  useEffect(() => {
    if (selectedItem) {
      setInputValue(selectedItem.label);
    } else {
      setInputValue('');
    }
  }, [selectedItem]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(true);

    const newFilteredItems = items.filter(item =>
      item.label.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredItems(newFilteredItems);
  };

  const handleItemClick = (item) => {
    setInputValue(item.label);
    onSelect(item);
    setShowDropdown(false);
  };

  const handleClickOutside = useCallback((event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowDropdown(false);
      if (selectedItem) {
        setInputValue(selectedItem.label);
      } else {
        setInputValue('');
      }
    }
  }, [selectedItem]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <input
        type="text"
        className="w-full bg-gray-500 text-white border-b-2 focus:outline-none focus:border-cyan-300 border-cyan-200 rounded-md p-1 text-center placeholder:text-center"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
      />
      {showDropdown && filteredItems.length > 0 && (
        <ul className="absolute z-10 w-full bg-gray-700 mt-1 rounded-md shadow-lg max-h-40 overflow-y-auto">
          {filteredItems.map((item, index) => (
            <li
              key={index}
              className="p-2 cursor-pointer hover:bg-gray-500 text-sm text-white"
              onClick={() => handleItemClick(item)}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const cleanObject = (obj) => { // clean object b4 sending to api. Make sure it does not contain empty items
    if (obj === null || obj === undefined) {return null;}
    if (typeof obj === 'object') {
      if (Array.isArray(obj)) {
        return obj
          .map(cleanObject)
          .filter(item => item !== null && item !== undefined && item !== '');
      } else {
        const cleanedObj = Object.fromEntries(
          Object.entries(obj)
              .map(([key, value]) => [key, cleanObject(value)])
              .filter(([key, value]) => value !== null && value !== undefined && value !== '')
        );
        return Object.keys(cleanedObj).length > 0 ? cleanedObj : null;
      }
    }
    return obj;
};
/**
 * 
 * Generic safe finder for any array of objects
 * @param {Object} itemToFind - the object to search for
 * @param {Array} dataArray - array of objects to search in
 * @param {Array} fields - array of field names to match (priority order)
 * @returns {Object|false} - returns the matching object if found, otherwise false
 */
export const findRecordSafe = (itemToFind, dataArray, fields = ['name']) => {
  if (!Array.isArray(dataArray) || !itemToFind) return false;

  const normalizedItem = {};
  fields.forEach(field => {
    normalizedItem[field] = itemToFind[field]?.toString().toLowerCase().trim() || '';
  });

  const found = dataArray.find(record => {
    for (let field of fields) {
      const recordValue = record[field]?.toString().toLowerCase().trim() || '';
      // Only compare if both values exist
      if (normalizedItem[field] && recordValue && normalizedItem[field] === recordValue) {
        return true;
      }
    }
    return false;
  });

  return found || false;
};


// utils/fetchImageAsBase64.js
export const fetchImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1]; // Remove the "data:*/*;base64," part
        const contentType = blob.type;
        resolve({ base64data, contentType });
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  } catch (error) {
    console.error("Failed to fetch image from URL", error);
    return { base64data: '', contentType: '' };
  }
};


export const getChangedFields = (original, updated) => {
  console.log('original', original);
  console.log('updated', updated);

  const changedFields = {};

  for (const key in updated) {
    if (
      Object.prototype.hasOwnProperty.call(updated, key) &&
      (
        !original.hasOwnProperty(key) || 
        !_.isEqual(original[key], updated[key])
      )
    ) {changedFields[key] = updated[key];}
  }

  // Always include 'id' from original if it exists
  if (original.hasOwnProperty('id')) {changedFields.id = original.id;}

  return changedFields;
};


export const deepEqual = (a, b) => { // check if original object is the same as current object
 
    if (a.length !== b.length) {
        return true; // Arrays have different lengths, so they have changed
    }

    // Sort both arrays by a unique identifier (product_id) to ensure the order doesn't affect comparison
    a = a.sort((a, b) => a.product_id - b.product_id);
    b = b.sort((a, b) => a.product_id - b.product_id);

    // Compare each object in the arrays
    for (let i = 0; i < a.length; i++) {
        const cleanedItem = a[i];
        const originalItem = b[i];

        // Check if any field is different between the two objects
        if (cleanedItem.product_id !== originalItem.product_id ||
            cleanedItem.description !== originalItem.description ||
            cleanedItem.unit_price !== originalItem.unit_price ||
            cleanedItem.discount_percentage !== originalItem.discount_percentage ||
            cleanedItem.quantity !== originalItem.quantity) {
            return true; // Found a difference, so the objects have changed
        }
    }

    // No differences found, so the objects are the same
    return false;
}




export const createOrder = (employee, cart,immediatePayment,billId) => { //create order  using items from the cart just before submitting the order 
  if (!employee) {return console.error('No employee selected');}
  const now = new Date();
  const shortName = `${employee.first_name.charAt(0)}${employee.last_name}-${formatShortDateTime(now)}`; // Concatenates the first letter of the first name with the last name
  const updatedOrder = {
    bill_id:billId,
    employee_id: employee.id,
    name: shortName,
    state: 'confirmed',
    transactionLines: cart.map(({ id, quantity }) => ({product_id: id,quantity: quantity,})),
    immediatePayment:immediatePayment,
  };
  return cleanObject(updatedOrder);
};

export const handleDownloadPDF = (quote,companyProfile) => {
  console.log(companyProfile)
  const doc = new jsPDF();
  const margin = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();


// Calculate the center of the page
const centerX = pageWidth / 1.5;
const centerY = pageHeight / 1.2;


// Assuming companyProfile.name contains the company name
const companyName = companyProfile.name;



// Apply a futuristic, elegant design to the watermark
doc.setFont('helvetica', 'bold');  // Use a modern, sleek font
doc.setFontSize(90);  // Increase the font size for a stunning watermark effect
//doc.setTextColor(44, 130, 201);  // Futuristic blue color for the text
doc.setTextColor(220, 220, 220); // light gray

// Apply a glowing effect using light cyan color for the outer layer (subtle glow)
//doc.setTextColor(0, 255, 255, 0.3);  // Semi-transparent cyan (outer glow effect)
//doc.setTextColor(169, 169, 169, 0.3);  // Semi-transparent gray

// Add the text as a watermark with elegant, futuristic features
doc.text(companyName, centerX, centerY, {
  align: 'center',  // Center the text horizontally
  angle: 45,        // Diagonal angle for the watermark effect
  baseline: 'middle',  // Vertically center the text as well
  opacity: 0.1  // Make the text faint but visible (watermark effect)
});

// Optional: Add a second layer of text with a slight shadow for depth and dimension
doc.setFontSize(90);  
//doc.setTextColor(0, 255, 255, 0.1);  // Slight shadow effect with a very faint cyan color
// doc.setTextColor(169, 169, 169, 0.3);
doc.setTextColor(200, 200, 200); // Use a medium-light gray


const topY = 30; // Unified Y position for both logo and PO heading
const LogotopY = 20; // Unified Y position for both logo and PO heading

// Logo
const logoUrl = companyProfile?.photo_url;
const imageType = logoUrl && logoUrl.match(/\.(jpeg|jpg|png)$/i)?.[1].toUpperCase() || 'JPEG'; // Default to JPEG if no match

// doc.addImage(logoBase64, 'PNG', margin, LogotopY, 40, 20); // Now starts at same Y as PO heading
doc.addImage(logoUrl, imageType, margin, LogotopY, 40, 20);  // Adjust dimensions as needed
// Company Address under logo
doc.setFontSize(10);
doc.setTextColor(80);
const companyAddress = [
  companyProfile.name,
  companyProfile.postal_address,
  `${companyProfile.physical_address}, ${companyProfile.city}`,
  `Telephone: ${companyProfile.phone_number}`,
  `Email: ${companyProfile.email}`,
];

companyAddress.forEach((line, i) => {
  if (line.includes('Telephone') || line.includes('Email')) {
    doc.setFont('Helvetica', 'bold');
  } else {
    doc.setFont('Helvetica', 'normal');
  }

  doc.text(line, margin, topY + 12 + i * 5); // Starts below the logo (20px height + padding)
});

// PURCHASE ORDER Heading
doc.setFontSize(22);
doc.setFont('helvetica', 'bold');
doc.setTextColor(44, 130, 201);
const metaX = pageWidth - margin - 80;
doc.text('PURCHASE ORDER', metaX, topY); // Aligned with logo top

// === PO Metadata ===
const metaY = topY + 6; // A little lower than heading
const metaLineHeight = 5;

doc.setFontSize(11);
doc.setTextColor(60);
doc.setFont('helvetica', 'bold');
doc.text('Reference:', metaX, metaY);
doc.text('Date:', metaX, metaY + metaLineHeight);

doc.setFont('courier', 'normal');
doc.setTextColor(20);
const referenceValue = quote.reference;
const dateValue = formatMediumDate(quote.date);

doc.text(referenceValue, pageWidth - margin, metaY, { align: 'right' });
doc.text(dateValue, pageWidth - margin, metaY + metaLineHeight, { align: 'right' });

 

  // Supplier Info Header
const supplierTitle = 'Supplier Information:';
const supplierTitleY = 75;
const fontSize = 11;
const paddingX = 4;
const textWidth = doc.getTextWidth(supplierTitle);
const boxWidth = textWidth + paddingX * 2;
const textHeight = 4.5;
const boxHeight = textHeight + 2;
const boxY = supplierTitleY - textHeight + 1;

doc.setFillColor(44, 130, 201);
doc.rect(margin, boxY, boxWidth, boxHeight, 'F');
doc.setFontSize(fontSize);
doc.setFont('helvetica', 'bold');
doc.setTextColor(255);
doc.text(supplierTitle, margin + paddingX, supplierTitleY);

// Supplier Details (no gap)
doc.setFont('helvetica', 'normal');
doc.setFontSize(10);
doc.setTextColor(50);
const supplier = quote?.partners || {};
const supplierLines = [
  `Name: ${supplier.name}`,
  `Contact: ${supplier.contact_person} (${supplier.contact_person_position})`,
  `Email: ${supplier.email}`,
  `Phone: ${supplier.phone_number}`
];

supplierLines.forEach((line, i) => {
  // Adjust Y position without adding a gap between the header and details
  doc.text(line, margin, supplierTitleY + boxHeight + i * 5); // Directly after the header
});

// Shipping Terms, Lead Time, and Delivery Date (placed after Supplier Info)
const termsAndDates = [
{ label: 'Shipping Terms', value: 'FOB Origin' },
{ label: 'Lead Time', value: '10-14 days' },
{ label: 'Delivery Date', value: 'TBD' }
];

// Adjust Y-position for Shipping Terms section, placing it right after Supplier Info
const shippingTermsY = supplierTitleY + 30; // Move closer to Supplier Info

doc.setFontSize(11);  // Reduced font size for more compact layout
doc.setTextColor(0);

// Set background for this section (reduce the height of the rectangle to compress the space)
doc.setFillColor(245, 245, 245); // Light gray background
doc.rect(margin, shippingTermsY, pageWidth - 2 * margin, 14, 'F'); // Adjusted height to 14

// Create blue background for the label row (reduce height for compact spacing)
const labelRowHeight = 6; // Reduce label row height for more compact appearance
doc.setFillColor(44, 130, 201); // Blue background for the label row
doc.rect(margin, shippingTermsY, pageWidth - 2 * margin, labelRowHeight, 'F'); // Blue background

// Draw the labels with white text (adjusted for closer positioning)
termsAndDates.forEach((term, index) => {
const startX = margin + index * (pageWidth - 2 * margin) / 3;
const columnWidth = (pageWidth - 2 * margin) / 3;

// Label positioning (reduce vertical gap between label and value)
doc.setFont('Helvetica', 'bold');
doc.setTextColor(255); // White text color for labels
doc.text(term.label, startX + 5, shippingTermsY + 4); // Adjusted Y to + 4 for closer positioning

// Value positioning (closer to the label)
doc.setFont('Helvetica', 'normal');
doc.setTextColor(0); // Black text color for values
doc.text(term.value, startX + 5, shippingTermsY + 10); // Adjusted Y to + 10 for closer positioning
});

  // Order Details Table
const tableHead = [[
  '#', 'Description', 'Qty', 'Unit Price', 'Discount', 'Total'
]];

const tableBody = quote.transactionLines.map((line, index) => [
  index + 1,
  line.description || '-',
  line.quantity,
  `${parseFloat(line.unitPriceInclusive).toFixed(2)}`,
  `${line.discount_percentage}%`,
  `${parseFloat(line.lineDiscountedPriceInclusive).toFixed(2)}`
]);

doc.autoTable({
  startY: supplierTitleY + 55,
  head: tableHead,
  body: tableBody,
  theme: 'striped',
  styles: {
    fontSize: 10,
    cellPadding: 3,
    lineWidth: 0.1,
    lineColor: [220, 220, 220],
    halign: 'center',
    valign: 'middle',
    cellWidth: 'auto',
    borderRadius: 4,
  },
  headStyles: {
    fillColor: [44, 130, 201],
    textColor: 255,
    fontStyle: 'bold',
    halign: 'center',
    borderRadius: 4,
  },
  alternateRowStyles: {
    fillColor: [245, 248, 255],
  },
  columnStyles: {
    0: { halign: 'left' },     // # column
    1: { halign: 'left' },     // Description column (updated to left)
    2: { halign: 'center' },   // Qty
    3: { halign: 'right' },    // Unit Price
    4: { halign: 'right' },    // Discount
    5: { halign: 'right' },    // Total
  },
  margin: { left: margin, right: margin },
});

// Summary Box with label and value columns clearly separated
const summaryY = doc.lastAutoTable.finalY + 2;
const boxX = 110;
const SummaryBoxWidth = pageWidth - boxX - margin;
const boxPadding = 2;
const lineHeight = 8;

// Data
const summaryLines = [
  ['Subtotal :', formatCurrency(quote.totalPriceExclusive)],
  ['Discount:', `- ${formatCurrency(quote.totalDiscountExclusive)}`],
  ['Total After Discount:', formatCurrency(quote.totalDiscountedPriceExclusive)],
  ['Tax:', formatCurrency(quote.totalTaxAfterDiscounted)],
  ['Grand Total :', formatCurrency(quote.totalDiscountedPriceInclusive)],
];

// Box background
const summaryBoxHeight = summaryLines.length * lineHeight + boxPadding * 2;
doc.setFillColor(245, 248, 255);
doc.roundedRect(boxX, summaryY, SummaryBoxWidth, summaryBoxHeight, 2, 2, 'F');

// Content inside the box
doc.setFontSize(10);
const defaultTextColor = 50;
doc.setTextColor(defaultTextColor);

summaryLines.forEach(([label, value], i) => {
  const y = summaryY + boxPadding + (i + 1) * lineHeight - 2;

  const isTotalLine = /total/i.test(label); // Check if label contains 'total' (case-insensitive)

  if (isTotalLine) {
    doc.setFont('helvetica', 'bold');
  } else {
    doc.setFont('helvetica', 'normal');
  }

  // Optionally change color for final total only
  if (label.includes('Grand Total')) {
    doc.setTextColor(0, 102, 204);
  } else {
    doc.setTextColor(defaultTextColor);
  }

  // Label on the left
  doc.text(label, boxX + boxPadding, y);

  // Value aligned right
  doc.text(value, boxX + SummaryBoxWidth - boxPadding, y, { align: 'right' });
});

// Sign-off Row: "Authorized By" & "Date"
const signoffY = doc.lastAutoTable.finalY + 75; // adjust if needed based on table height
const signoffX = margin;
const signoffWidth = pageWidth - margin * 2;
const sectionHeight = 20;

// Styling
doc.setDrawColor(200);
doc.setLineWidth(0.1);
doc.setFontSize(10);
doc.setFont('helvetica', 'normal');
doc.setTextColor(50);

// Labels
doc.text('Authorized By:', signoffX, signoffY);
doc.text('Date:', signoffX + signoffWidth / 2, signoffY);

// Signature lines
doc.line(signoffX, signoffY + 5, signoffX + signoffWidth / 2 - 10, signoffY + 5); // line under "Authorized By"
doc.line(signoffX + signoffWidth / 2, signoffY + 5, signoffX + signoffWidth - 10, signoffY + 5); // line under "Date"

  // Footer
  // doc.setFontSize(9);
  // doc.setTextColor(150);
  // doc.text('Generated by Purchase System • ' + new Date().toLocaleDateString(), margin, 285);

  doc.save(`PurchaseOrder-${quote.reference || 'unknown'}.pdf`);
};

export const generateReceiptHTML = (data, companyProfile,status) => {

  if (!data || !data.billOrders || data.billOrders.length === 0) {
    return '<html><head><title>No Data Available</title></head><body><h1>No Receipt Data Available</h1><p>Please check back later.</p></body></html>';
  }

  let html = '<html><head><title>Print Bill</title>';
  html += '<style>';
  html += 'body{font-family: Courier, monospace; font-size: 10px; width: 100%; margin: 0; padding: 0; text-align: center; position: relative;}';
  html += 'h1{font-size: 14px;}';
  html += 'h2{font-size: 12px; margin: 5px 0;}';
  html += 'h3{font-size: 11px; margin: 0;}';
  html += 'table{border-collapse: collapse; padding: 4px; width: auto; margin: 0 auto; position: relative;}';
  html += 'th, td{border: 0.5px dashed #000; padding: 4px; text-align: left; font-size: 10px; position: relative;}';
  html += 'th{text-align: center; font-weight: bold;}';
  html += 'tr:nth-child(even){background: #f0f0f0;}';
  html += 'p{font-size: 10px; margin: 5px 0;}';
  html += '.divider{border-top: 0.5px dashed #000; margin: 10px 0;}';
  html += '.status{background-color: #f8d7da; color: #721c24; font-weight: bold; padding: 10px; margin: 10px 0;}';
  html += '.watermark-container{position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 100%; height: 100%; pointer-events: none; display: flex; align-items: center; justify-content: center;}';
  html += '.watermark{font-size: 50px; color: rgba(255, 0, 0, 0.2); transform: rotate(-45deg);}';
  html += 'td.product{max-width: 20em; overflow: hidden; text-overflow: ellipsis;}';
  html += '</style>';
  html += '</head><body>';

  // Add Company Info
  html += '<h1>' + (companyProfile?.name || '') + '</h1>'; // Company name
  html += '<p>' + (companyProfile?.physical_address || '') + '<br>' + (companyProfile?.city || '') + ', ' + (companyProfile?.country || '') + '<br>Phone: ' + (companyProfile?.phone_number || '') + '</p>'; // Replace with actual address

  // Add Bill Header
  html += '<h2>' + (status ? 'Customer Receipt' : 'Customer Bill') + '</h2>';

  html += '<p>Sales Person: ' + (data.employees?.first_name || '') + ' ' + (data.employees?.last_name || '') + '<br>' +
          'Bill #: ' + (data.reference || '') + '<br>' +
          'Date: ' + new Date(data.date).toLocaleString() + '<br>' +
          'Amount Due: ' + formatCurrency(data.billOrders.reduce((acc, order) => acc + order.totalTaxedPrice, 0)) + '</p>';

  // Initialize aggregates
  let totalUntaxedPrice = 0;
  let totalTax = 0;
  let totalTaxedPrice = 0;


  // Loop through each order
  data.billOrders.forEach((order, orderIndex) => {
      let orderTotal = 0; // Initialize order total

      // Add Order Header
      html += '<h3>Order ' + (orderIndex + 1) + '</h3>';
      html += '<table>'; // Start order table

      // Add Watermark container inside the table
      html += '<div class="watermark-container">';
      if (!data.paid) {
          html += '<div class="watermark">UNPAID</div>'; // Watermark for unpaid bills
      } else {
          html += '<div class="watermark">PAID</div>'; // Watermark for paid bills
      }
      html += '</div>'; 

      html += '<thead>';
      html += '<tr><th>Product</th><th>Qty</th><th>Total</th></tr>'; // Remove Unit Price column
      html += '</thead>';
      html += '<tbody>';

      // Check if transactionLines exists
      if (order.transactionLines) {
          // Add Order Details
          order.transactionLines.forEach(line => {
              const unitPrice = parseFloat(line.unit_price); // Convert to number
              const total = parseFloat(line.line_total_taxed); // Convert to number
              
              html += '<tr>';
              html += '<td class="product">' + (line.products?.name || 'Unknown Product') + ' @ ' + formatCurrency(unitPrice) + '</td>';
              html += '<td style="text-align: center;">' + parseFloat(line.quantity).toFixed(2) + '</td>';
              html += '<td style="text-align: right;">' + formatCurrency(total) + '</td>';
              html += '</tr>';

              orderTotal += total; // Add to order total
          });
      } else {
          html += '<tr><td colspan="3">No transaction lines available</td></tr>';
      }

      html += '</tbody>';
      html += '</table>';

      // Print Order Total as part of the order section
      html += '<p><strong>Total for Order ' + (orderIndex + 1) + ': ' + formatCurrency(orderTotal) + '</strong></p>';

      // Update aggregates
      totalUntaxedPrice += order.totalUntaxedPrice || 0;
      totalTax += order.totalTax || 0;
      totalTaxedPrice += order.totalTaxedPrice || 0;
  });

  // Add Footer with the divider below the entire bill
  html += '<br><p><strong>Total Untaxed: ' + formatCurrency(totalUntaxedPrice) + '</strong></p>';
  html += '<p><strong>Total Tax: ' + formatCurrency(totalTax) + '</strong></p>';
  html += '<p><strong>Total Amount Due: ' + formatCurrency(totalTaxedPrice) + '</strong></p>';
  html += '<br><p>Thank you for your purchase!</p>';
  html += '<div class="divider"></div>';
  html += '</body></html>';
  
  return html;
};

export const handlePrint = (printData,companyProfile,status) => {

  const printWindow = window.open('', '', 'height=600,width=400'); // Adjust width as needed
  const receiptHTML = generateReceiptHTML(printData,companyProfile,status);
  printWindow.document.write(receiptHTML);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

export const generateQuotationHtml = (quotation) => {
  const productRows = quotation.products.map((product, index) => (
    `<tr>
        <td>${index + 1}</td>
        <td>${product.name || 'N/A'}</td>
        <td>${formatNumber(product.price).toFixed(2)}</td>
        <td>${formatNumber(product.qty).toFixed(2)}</td>
        <td>${formatNumber(product.discount).toFixed(2)}</td>
        <td class="total">${formatNumber(product.total).toFixed(2)}</td>
    </tr>`
  )).join('');

  const totalAmount = formatNumber(quotation.products.reduce((total, product) => total + product.total, 0)).toFixed(2);

  return `
    <html>
    <head>
    <style>
    /* Include the styles you have for the HTML content */
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      margin: 0;
      padding: 0;
      color: #333;
      background-color: #f4f4f4;
    }
    .container {
      width: 800px;
      margin: 0 auto;
      background-color: #fff;
      padding: 30px;
      border-radius: 8px;
      box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
    }
    /* Print Styles */
    @media print {
      body {
        background-color: #fff;
      }
      .container {
        width: 100%;
        margin: 0;
        padding: 10px;
        box-shadow: none;
      }
      table {
        width: 100%;
        font-size: 10px;
      }
      .footer {
        page-break-before: always;
        text-align: center;
        font-size: 10px;
      }
      .no-print {
        display: none;
      }
    }
    /* General print styles */
    .invoice-header, .invoice-info, .payment-and-totals {
      margin-bottom: 20px;
    }
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px;
      background-color: #4CAF50;
      color: white;
      border-radius: 8px;
    }
    .invoice-info {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
    }
    .customer-info {
      text-align: right;
    }
    table {
      border-collapse: collapse;
      width: 100%;
    }
    table th, table td {
      border: 1px solid #ddd;
      padding: 8px;
      font-size: 10px;
    }
    table th {
      background-color: #4CAF50;
      color: white;
      text-align: left;
    }
    table td.total {
      text-align: right;
    }
    .payment-and-totals {
      display: flex;
      justify-content: space-between;
      margin-top: 10px;
    }
    .payment-info, .labels-section, .totals-section {
      font-size: 12px;
    }
    .footer {
      margin-top: 20px;
    }
    </style>
    </head>
    <body>
    <div class="container">
      <!-- Invoice Header with Logo and Info -->
      <div class="invoice-header">
        <div class="company-info">
          <img src="${quotation.companyProfile.photoUrl}" alt="Company Logo">
          <ul>
            <li><strong>${quotation.companyProfile.name}</strong></li>
            <li>${quotation.companyProfile.physical_address}</li>
            <li>${quotation.companyProfile.city} ${quotation.companyProfile.country}</li>
            <li>Email: ${quotation.companyProfile.email}</li>
            <li>Telephone: ${quotation.companyProfile.phone_number}</li>
          </ul>
        </div>
        <div>
          <h1>INVOICE</h1>
        </div>
      </div>

      <!-- Invoice and Customer Information -->
      <div class="invoice-info">
        <div>
          <p><strong>INVOICE NO: ${quotation.invoiceNumber}</strong></p>
          <p>Date: ${quotation.date}</p>
        </div>
        <div class="customer-info">
          <p><strong>Invoice To:</strong></p>
          <p id="customerName">${quotation.customer.name}</p>
          <p>${quotation.customer.physical_address}</p>
          <p>${quotation.customer.city}, ${quotation?.customer?.countries?.name}</p>
          <p>${quotation.customer.phone_number}</p>
        </div>
      </div>

      <!-- Products Table -->
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Product Name</th>
            <th>Price</th>
            <th>Quantity</th>
            <th>Discount (%)</th>
            <th class="total">Total</th>
          </tr>
        </thead>
        <tbody>
        ${productRows}
        </tbody>
      </table>

      <!-- Payment Info and Totals -->
      <div class="payment-and-totals">
        <div class="payment-info">
          <p><strong>Payment Info:</strong></p>
          <p>Bank: XYZ Bank</p>
          <p>Account Name: ABC Corp</p>
          <p>Account Number: 987654321</p>
        </div>

        <div class="labels-section">
          <p><strong>SUB TOTAL</strong></p>
          <p><strong>TAX (10%):</strong> </p>
          <p><strong>GRAND TOTAL:</strong> </p>
        </div>

        <div class="totals-section">
          <p><strong>MK${totalAmount}</strong></p>
          <p><strong>MK${(totalAmount * 0.1).toFixed(2)}</strong></p>
          <p><strong>MK${(totalAmount * 1.1).toFixed(2)}</strong></p>
        </div>
      </div>

      <div class="payment-and-totals">
        <div class="terms-info">
          <p><strong>Terms and Conditions:</strong></p>
          <p>Payment is due within 30 days of the invoice date.</p>
          <p>Late payments will incur a 5% fee.</p>
        </div>

        <div>
          <div class="terms-info">
            <p><strong>Thank you for your business!</strong></p>
            <p>For any inquiries, contact us at <a href="mailto:${quotation?.companyProfile?.email}">${quotation?.companyProfile?.email}</a></p>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="footer">
        <!-- Footer content here -->
      </div>
    </div>
    </body>
    </html>
  `;
};

 export const printQuotation = (quotation) => {


  const printWindow = window.open('', '', 'height=800,width=1200');
  if (!printWindow) {
      console.error('Failed to open print window.');
      return;
  }

  const productRows = quotation.products.map((product, index) => (
      `<tr>
          <td>${index + 1}</td>
          <td>${product.name || 'N/A'}</td>
          <td>${formatNumber(product.price).toFixed(2)}</td>
          <td>${formatNumber(product.qty).toFixed(2)}</td>
          <td>${formatNumber(product.discount).toFixed(2)}</td>
          <td class="total">${formatNumber(product.total).toFixed(2)}</td>
      </tr>`
  )).join('');

  const totalAmount = formatNumber(quotation.products.reduce((total, product) => total + product.total, 0)).toFixed(2);

  printWindow.document.write(`
      <html>
      <head>
      <style>
/* General Styles */
body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    margin: 0;
    padding: 0;
    color: #333;
    background-color: #f4f4f4;
}

.container {
  width: 800px;
  margin: 0 auto;
  background-color: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
}

/* Print Styles */
@media print {
    body {
        background-color: #fff;
    }

    .container {
        width: 100%;
        margin: 0;
        padding: 10px;
        box-shadow: none;
    }

    /* Prevent page breaks inside container */
    .container, .invoice-header, .invoice-info, .payment-and-totals {
        page-break-inside: avoid;
    }

    /* Force table to fit */
    table {
        width: 100%;
        font-size: 10px; /* Reduce font size for better fit */
    }

    /* Ensure headers and footers are not cut off */
    .footer {
        page-break-before: always;
        text-align: center;
        font-size: 10px;
    }

    /* Hide elements that are not needed in print */
    .no-print {
        display: none;
    }
}

/* General print styles */
.invoice-header, .invoice-info, .payment-and-totals {
    margin-bottom: 20px;
}

.invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0px;
    background-color: #4CAF50;
    color: white;
    border-radius: 8px;
}

.invoice-info {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
}

.customer-info {
    text-align: right;
}

table {
    border-collapse: collapse;
    width: 100%;
}

table th, table td {
    border: 1px solid #ddd;
    padding: 8px;
    font-size: 10px; /* Smaller font size for better fit */
}

table th {
    background-color: #4CAF50;
    color: white;
    text-align: left;
}

table td.total {
    text-align: right;
}

.payment-and-totals {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
}

.payment-info, .labels-section, .totals-section {
    font-size: 12px;
}

.footer {
    margin-top: 20px;
}
</style>

      </head>
      <body>
      <div class="container">
        <!-- Invoice Header with Logo and Info -->
        <div class="invoice-header">
          <div class="company-info">
              <img src="${quotation.companyProfile.photoUrl}" alt="Company Logo">
              <ul>
                  <li><strong>${quotation.companyProfile.name}</strong></li>
                  <li>${quotation.companyProfile.physical_address}</li>
                  <li>${quotation.companyProfile.city} ${quotation.companyProfile.country}</li>
                  <li>Email: ${quotation.companyProfile.email}</li>
                  <li>Telephone: ${quotation.companyProfile.phone_number}</li>
              </ul>
          </div>
        <div>
              <h1>INVOICE</h1>
          </div>
      </div>

      <!-- Invoice and Customer Information -->
      <div class="invoice-info">
          <div>
              <p><strong>INVOICE NO: ${quotation.invoiceNumber}</strong></p>
              <p>Date: ${quotation.date}</p>
          </div>
          <div class="customer-info">
              <p><strong>Invoice To:</strong></p>
              <p id="customerName">${quotation.customer.name}</p>
              <p>${quotation.customer.physical_address}</p>
              <p>${quotation.customer.city}, ${quotation?.customer?.countries?.name}</p>
              <p>${quotation.customer.phone_number}</p>
          </div>
      </div>

      <!-- Products Table -->
      <table>
          <thead>
              <tr>
                  <th>#</th>
                  <th>Product Name</th>
                  <th>Price</th>
                  <th>Quantity</th>
                  <th>Discount (%)</th>
                  <th class="total">Total</th> <!-- Ensure the class is applied here -->
              </tr>
          </thead>
          <tbody>
          ${productRows}
          </tbody>
      </table>

      <!-- Payment Info and Totals -->
      <div class="payment-and-totals">
          <div class="payment-info">
              <p><strong>Payment Info:</strong></p>
              <p>Bank: XYZ Bank</p>
              <p>Account Name: ABC Corp</p>
              <p>Account Number: 987654321</p>
          </div>

          <div class="labels-section">
              <p><strong>SUB TOTAL</strong></p>
              <p><strong>TAX (10%):</strong> </p>
              <p><strong>GRAND TOTAL:</strong> </p>
          </div>

          <div class="totals-section">
              <p><strong>MK${totalAmount}</strong></p>
              <p><strong>MK${(totalAmount * 0.1).toFixed(2)}</strong></p>
              <p><strong>MK${(totalAmount * 1.1).toFixed(2)}</strong></p>
          </div>
      </div>

      <div class="payment-and-totals">
          <div class="terms-info">
              <p><strong>Terms and Conditions:</strong></p>
              <p>Payment is due within 30 days of the invoice date.</p>
              <p>Late payments will incur a 5% fee.</p>
          </div>

          <div>
            <div class="terms-info">
              <p><strong>Thank you for your business!</strong></p>
              <p>For any inquiries, contact us at <a href="mailto:${quotation?.companyProfile?.email}">${quotation?.companyProfile?.email}</a></p>

            <div class="terms-info">
          </div>
      </div>
      
      <!-- Footer -->
      <div class="footer">
          <!-- Footer content here -->
      </div>
      
      </div>
  
      <script>
          window.print();
          window.onafterprint = function() { window.close(); };
      </script>
      </body>
      </html>
  `);
};




  export const handleApi = async (page, changedFields, userData, apiMethod, parameters) => { // connect to api for CRUD operations 
    console.log('payment payload sir ',changedFields);
    const config = {
      headers: {  Authorization: userData && userData.data && userData.data.token 
        ? `Bearer ${userData.data.token}` : '' },
       params: parameters ? { ...parameters } : {}, // Spread `changedFields` to flatten its properties
    };


  if(apiMethod==='POST'){

  }
  
    try {
      let response;
  
      switch (apiMethod) {
        case 'PUT':
          response = await axios.put(`${API_BASE_URL}/${page}`, changedFields, config);
          break;
        case 'POST':
       
          response = await axios.post(`${API_BASE_URL}/${page}`, changedFields, config);
          break;
        case 'DELETE':
          response = await axios.delete(`${API_BASE_URL}/${page}`, config);
          break;
        case 'GET': // New case for fetching data
          // Merge any additional query parameters in `changedFields` with `config.params`
         // config.params = { ...config.params, ...parameters };
          
          response = await axios.get(`${API_BASE_URL}/${page}`, config);
          break;
        default:
          throw new Error('Unsupported method');
      }

      return response; // Return the full response object
    } catch (err) {
      console.error(`Error handling API request:`, err);
      throw err; // Rethrow the actual error
    }
  };
  